// Website enquiries - record shape (rows come from /api/enquiries) plus the
// per-form field map the Leads dashboard renders. Option lists below are the
// static mirror used when the API is unreachable (see lib/enquiry.ts).
import type { EnrollmentRequestRecord, PortalEnquiry } from "./api";
import type { LeadSource, LeadStatus } from "./types";

export type EnquiryKind =
  | "general"
  | "travel"
  | "personality"
  | "it"
  | "corporate"
  | "ielts";

export type Enquiry = Omit<PortalEnquiry, "kind" | "source" | "status"> & {
  kind: EnquiryKind;
  source: LeadSource;
  status: LeadStatus;
  /** Approve / reject details; only sent to staff who can open Students. */
  enrollment_request?: EnrollmentRequestRecord;
};

/* ---------------------------------------------------------- option lists */

export const interestedCourses = [
  "Travel & Tourism",
  "Personality Development",
  "IT Courses",
  "Corporate Training",
  "IELTS",
];

export const travelCourseOptions = [
  "IATA – Foundation in Travel & Tourism Diploma",
  "Travel & Tourism Certificate Course",
];

export const itCourseOptions = ["Gen AI", "DM & AI", "AI & Cybersecurity", "Claude Mastery"];

export const corporateProgrammes = [
  "Upskilling",
  "Communication",
  "Sales Training",
  "Professional Grooming",
  "Workplace Resilience & Stress Management",
  "Team Building",
  "Productivity",
  "Cross-Generational Collaboration",
  "Other",
];

export const travelProgrammes = [
  "IATA – Foundation in Travel & Tourism Diploma",
  "MVA – Foundation in Travel & Tourism Certificate",
];

export const batchOptions = ["Weekdays", "Weekends"];

export const trainingModes = ["At MVA", "At Your Organisation", "Other"];

export const enquiryStatuses: LeadStatus[] = ["New", "Contacted", "Converted", "Lost"];

export const enquiryTypeLabel: Record<EnquiryKind, string> = {
  general: "General Enquiry",
  travel: "Travel & Tourism",
  personality: "Personality Development",
  it: "IT Courses",
  corporate: "Corporate Training",
  ielts: "IELTS",
};

/* ------------------------------------------------------------ field map */

export type EnquiryValue = string | string[] | boolean | null | undefined;

export interface EnquiryField {
  key: string;
  label: string;
  cell?: "long" | "chips" | "consent";
  get: (e: Enquiry) => EnquiryValue;
}

const fName: EnquiryField = { key: "name", label: "Name", get: (e) => e.name };
const fPhone: EnquiryField = { key: "phone", label: "Mobile number", get: (e) => e.phone };
const fEmail: EnquiryField = { key: "email", label: "Email", get: (e) => e.email };
const fCity: EnquiryField = { key: "city", label: "City", get: (e) => e.city };
const fBatch: EnquiryField = { key: "batch", label: "Preferred batch", get: (e) => e.batch };
const fMessage: EnquiryField = {
  key: "message",
  label: "Message",
  cell: "long",
  get: (e) => e.message,
};
const fConsent: EnquiryField = {
  key: "consent",
  label: "Consent",
  cell: "consent",
  get: (e) => e.consent,
};

/** Single select, auto-filled programme, or multi-select list - renders as chips when array. */
function fProgramme(label: string): EnquiryField {
  return { key: "programme", label, cell: "chips", get: (e) => e.programmes ?? e.programme };
}

export interface EnquiryTab {
  id: EnquiryKind | "all";
  label: string;
  formName: string;
  fields: EnquiryField[];
}

export const enquiryTabs: EnquiryTab[] = [
  {
    id: "all",
    label: "All",
    formName: "Every website form",
    fields: [
      fName,
      { key: "kind", label: "Enquiry type", get: (e) => enquiryTypeLabel[e.kind] },
      fPhone,
      fEmail,
      {
        key: "summary",
        label: "Programme",
        cell: "chips",
        get: (e) => e.programmes ?? e.programme ?? e.interestedCourse,
      },
      { key: "city", label: "City / Location", get: (e) => e.city ?? e.location },
      // Visitor's message (or a corporate training requirement) - truncated in the table, full text in the detail view.
      { key: "message", label: "Message", cell: "long", get: (e) => e.message ?? e.requirement },
    ],
  },
  {
    id: "general",
    label: "General",
    formName: "1. General Enquiry",
    fields: [
      fName,
      fPhone,
      fEmail,
      fCity,
      { key: "interestedCourse", label: "Interested Courses", get: (e) => e.interestedCourse },
      fProgramme("Course"),
      fMessage,
      fConsent,
    ],
  },
  {
    id: "travel",
    label: "Travel & Tourism",
    formName: "2. Travel & Tourism Course Enquiry",
    fields: [
      fName,
      fPhone,
      fEmail,
      fCity,
      fProgramme("Preferred training programme"),
      fBatch,
      fMessage,
      fConsent,
    ],
  },
  {
    id: "personality",
    label: "Personality Development",
    formName: "3. Personality Development Enquiry",
    fields: [
      fName,
      fPhone,
      fEmail,
      fCity,
      fProgramme("Preferred training programme"),
      fBatch,
      fMessage,
      fConsent,
    ],
  },
  {
    id: "it",
    label: "IT Courses",
    formName: "4. IT Courses Enquiry",
    fields: [
      fName,
      fPhone,
      fEmail,
      fCity,
      fProgramme("Preferred training programme"),
      fBatch,
      fMessage,
      fConsent,
    ],
  },
  {
    id: "corporate",
    label: "Corporate Training",
    formName: "5. Corporate Training Enquiry",
    fields: [
      { key: "company", label: "Company name", get: (e) => e.company },
      { key: "name", label: "Your name", get: (e) => e.name },
      { key: "designation", label: "Designation", get: (e) => e.designation },
      fEmail,
      fPhone,
      { key: "location", label: "Location", get: (e) => e.location },
      fProgramme("Preferred training programme"),
      { key: "mode", label: "Training mode", get: (e) => e.mode },
      { key: "requirement", label: "Training requirement", cell: "long", get: (e) => e.requirement },
      fConsent,
    ],
  },
  {
    id: "ielts",
    label: "IELTS",
    formName: "6. IELTS Enquiry",
    fields: [
      fName,
      fPhone,
      fEmail,
      fCity,
      fProgramme("Preferred training programme"),
      fBatch,
      fMessage,
      fConsent,
    ],
  },
];

